import { Extension, type Editor } from '@tiptap/core';
import { TAMANHOS } from '../estilos';
import { formatoNoCursor } from './formatoParagrafo';

/** Ações que abrem painel ou dependem da tela: o editor decide o que fazer. */
export type AcaoDeTela = 'localizar' | 'substituir' | 'ortografia' | 'pincel' | 'caixa' | 'colarTexto' | 'link' | 'salvar';

export interface OpcoesAtalhos {
  aoAcionar: (acao: AcaoDeTela) => void;
}

/**
 * ATALHOS DO WORD EM PORTUGUÊS.
 *
 * Os mesmos que a tooltip de cada botão mostra. Três o navegador não deixa
 * página nenhuma capturar — Ctrl+N (nova janela), Ctrl+T (nova aba) e Ctrl+W
 * (fechar) —; por isso o Negrito também responde a Ctrl+B e o Selecionar tudo
 * a Ctrl+A, e a tooltip diz isso.
 *
 * Prioridade alta: o Sublinhado do StarterKit usa Ctrl+U, que no Word pt-BR é
 * Substituir.
 */
export const AtalhosWord = Extension.create<OpcoesAtalhos>({
  name: 'atalhosWord',
  priority: 1000,
  addOptions() {
    return { aoAcionar: () => {} };
  },
  addKeyboardShortcuts() {
    const ed = (): Editor => this.editor;
    const tela = (a: AcaoDeTela) => () => { this.options.aoAcionar(a); return true; };
    const ajustar = (f: (fmt: ReturnType<typeof formatoNoCursor>['formato']) => Record<string, unknown>) => () =>
      ed().chain().focus().ajustarParagrafo(f(formatoNoCursor(ed()).formato)).run();
    const tamanho = (passo: 1 | -1) => () => {
      const atual = formatoNoCursor(ed()).formato.tamanho;
      const i = TAMANHOS.indexOf(atual);
      const prox = TAMANHOS[Math.max(0, Math.min(TAMANHOS.length - 1, (i < 0 ? TAMANHOS.indexOf(12) : i) + passo))];
      return ed().state.selection.empty
        ? ed().chain().focus().ajustarParagrafo({ tamanho: prox }).run()
        : ed().chain().focus().setFontSize(`${prox}pt`).run();
    };

    return {
      'Mod-n': () => ed().chain().focus().toggleBold().run(),
      'Mod-s': () => ed().chain().focus().toggleUnderline().run(),
      'Mod-u': tela('substituir'),
      'Mod-l': tela('localizar'),
      'Mod-r': () => ed().chain().focus().redo().run(),
      'Mod-q': ajustar(() => ({ alinhamento: 'left' })),
      'Mod-e': ajustar(() => ({ alinhamento: 'center' })),
      'Mod-g': ajustar(() => ({ alinhamento: 'right' })),
      'Mod-j': ajustar(() => ({ alinhamento: 'justify' })),
      'Mod-1': ajustar(() => ({ entrelinha: 1 })),
      'Mod-5': ajustar(() => ({ entrelinha: 1.5 })),
      'Mod-2': ajustar(() => ({ entrelinha: 2 })),
      'Mod-0': ajustar((f) => ({ espacoAntes: f.espacoAntes ? 0 : 12 })),
      'Mod-m': ajustar((f) => ({ recuoEsquerdo: Math.min(10, f.recuoEsquerdo + 1.25) })),
      'Mod-Shift-m': ajustar((f) => ({ recuoEsquerdo: Math.max(0, f.recuoEsquerdo - 1.25) })),
      'Mod-Space': () => ed().chain().focus().limparFormatacao().run(),
      'Mod-Enter': () => ed().chain().focus().inserirQuebra('pagina').run(),
      'Mod-Shift-Enter': () => ed().chain().focus().inserirQuebra('coluna').run(),
      'Mod-Alt-f': () => ed().chain().focus().inserirNota().run(),
      'Mod-Shift->': tamanho(1),
      'Mod-Shift-.': tamanho(1),
      'Mod-Shift-<': tamanho(-1),
      'Mod-Shift-,': tamanho(-1),
      'Mod-=': () => ed().chain().focus().toggleSubscript().run(),
      'Mod-Shift-+': () => ed().chain().focus().toggleSuperscript().run(),
      'Mod-Shift-l': () => ed().chain().focus().toggleBulletList().run(),
      'Mod-Shift-c': tela('pincel'),
      'Mod-Shift-v': tela('colarTexto'),
      'Mod-k': tela('link'),
      // Salvar do Word pt-BR é Ctrl+B — aqui é o Negrito (o navegador não cede o Ctrl+N).
      // Shift+F12 é o outro Salvar do próprio Word.
      'Shift-F12': tela('salvar'),
      'Shift-F3': tela('caixa'),
      F7: tela('ortografia'),
    };
  },
});
